import Icon from '../../components/Icon/Icon';
import { Button, Checkbox, Dropdown, Input, Menu, Popover, Select, Tabs, Tag as AntdTag } from 'antd';
import ProfileCard from '../../components/Cards/ProfileCard';
import styles from './ResourceDetail.module.scss';
import { useResourceDetail } from './useResourceDetail';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import Tag from '../../components/Tag/Tag';
import { Controller, ControllerRenderProps } from 'react-hook-form';
import TextArea from 'antd/lib/input/TextArea';
import { FormField, Comment, Role, Status, ResourceType } from './ResourceDetail.model';
import Popup from '../../components/Popup/Popup';
import Spinner from '../../components/Spinner/Spinner';
import MediaPlayer from '../../components/MediaPlayer/MediaPlayer';
import moment from "moment";

const ResourceDetail = () => {
	const {
		addComment,
		addParticipant,
		canAddApplicantMentees,
		control,
		deleteComment,
		deleteResource,
		editComment,
		getBreadCrumbLink,
		handleSubmit,
		isAdmin,
		isAllowedToEdit,
		isAllowedToEditComment,
		isCreator,
		publishResource,
		onCancelForm,
		onClickUser,
		onCommentsInputChange,
		onDownload,
		onLoadMoreComments,
		onMediaReady,
		onSetCommentEditId,
		onSubmitForm,
		removeParticipant,
		resourceInformationFields,
		setEditCommentId,
		state,
		toggleCommentInput,
		toggleDeletePopup,
		toggleEditForm,
		toggleMediaPopup,
		unpublishResource
	} = useResourceDetail();
	const {
		currentUserFullName,
		currentUserImageId,
		editCommentId,
		loading,
		members,
		mentoringScopes,
		newComment,
		participants,
		resource,
		resourceComments,
		showCommentInput,
		showDeletePopup,
		showEditForm,
		showLoadMoreButton,
		showMediaPopup
	} = state;
	const intl = useIntl();

	const getTagColorByRole = (role: Role) => {
		switch (role) {
			case 'Mentee': return 'grey';
			case 'Mentor': return 'blue';
			case 'Admin': return 'red';
		}
	}
	
	const renderComments = (comments: Comment[]) => {
		return <div className={styles.commentList} >{comments.map((comment: Comment, index) =>
			editCommentId === comment.id ?
				renderCommentInput({
					fullName: `${comment.createdBy.firstName} ${comment.createdBy.lastName}`,
					imageUrl: comment.createdBy.imageUrl
				},
					() => { setEditCommentId('') },
					editComment) :
				<div className={styles.comment} key={comment.id}>
					<div className={styles.avatar} onClick={onClickUser.bind(null, comment.createdBy)}>
						{comment.createdBy.imageUrl && !comment.createdBy.isDeleted ? <img src={comment.createdBy.imageUrl} alt="" /> :
							<Icon name="user" color="white" width="16px" />}
					</div>
					<div>
						<h4 onClick={onClickUser.bind(null, comment.createdBy)}>
							{!comment.createdBy.isDeleted ?
								<>{comment.createdBy.firstName} {comment.createdBy.lastName}</> :
								<FormattedMessage id="userLabels.deletedUser" />}
						</h4>
						{comment.createdBy.role === "Admin" && <Tag color="red"><FormattedMessage id="role.Admin" /></Tag>}
						<p>{comment.content}</p>
						<span className={styles.date}>{intl.formatDate(comment.createdAt, { day: "numeric", hour: "2-digit", minute: "2-digit", month: "long", year: "numeric" })}</span>
					</div>
					{isAllowedToEditComment(comment) && <Dropdown overlay={<Menu>
						<Menu.Item key="1">
							<Button onClick={onSetCommentEditId.bind(null, comment)} type="link"><FormattedMessage id="buttons.edit" /></Button>
						</Menu.Item>
						<Menu.Item key="2">
							<Button className={styles.redBtn} onClick={() => { deleteComment(comment.id) }} type="link">
								<FormattedMessage id="buttons.delete" />
							</Button>
						</Menu.Item>
					</Menu>}>
						<div className={styles.iconWrapper} ><Icon name="ellipsis-h" color="text" /> </div>
					</Dropdown>}
				</div>)}
			{showLoadMoreButton && <Button type="link" className={styles.loadMore} onClick={onLoadMoreComments}><FormattedMessage id="buttons.loadMore"/></Button>}
		</div>
	}

	const renderCommentInput = (
		user: { imageUrl: string; fullName: string },
		onCancel: () => void,
		onSubmit: () => void
	) => {
		const { imageUrl, fullName } = user;
		return (
			<div className={styles.comment}>
				<div className={styles.avatar}>
					{imageUrl ? (
						<img src={imageUrl} alt="" />
					) : (
						<Icon name="user" color="white" width="16px" />
					)}
				</div>
				<div className={styles.commentContent}>
					<h4>{fullName}</h4>
					<TextArea
						autoSize={{ maxRows: 10, minRows: 4 }}
						rows={10}
						name="comment"
						onChange={onCommentsInputChange}
						placeholder={intl.formatMessage({id: "resources.commentPlaceholder"})}
						value={newComment}
					/>
					<div className={styles.commentBtns}>
						<Button size="large" type="default" onClick={onCancel}>
							<FormattedMessage id="buttons.cancel" />
						</Button>
						<Button
							className={styles.blueTextBtn}
							size="large"
							type="default"
							onClick={onSubmit}
						>
							<FormattedMessage id="buttons.post" />
						</Button>
					</div>
				</div>
			</div>
		);
	};

	const getIconByResourceType = (resourceType: string | undefined) => {
		switch (resourceType) {
			case 'Audio': return <Icon name="microphone" color="white" type="solid" height="100px" width="100px" />
			case 'Video': return <Icon name="video" color="white" type="solid" height="100px" width="100px" />
			case 'Text': return <Icon name="file-alt" color="white" type="solid" height="100px" width="100px" />
		}
	}

	const renderAuthor = () => {
		const author = resource?.author;
		return author && (
			<div className={styles.participant}  onClick={onClickUser.bind(null, author)}>
				<div className={styles.avatar}>
					{author?.imageUrl && !author.isDeleted ? (
						<img src={author?.imageUrl} alt="" />
					) : (
						<Icon name="user" color="white" width="12px" height="12px" />
					)}
				</div>
				<div>
					<span>
						{!author?.isDeleted ? <>{author?.firstName} {author?.lastName}</> : <FormattedMessage id="userLabels.deletedUser" />}
					</span>
					<div>
						<Tag color={'grey'}>
							<FormattedMessage id="resources.author" />
						</Tag>
					</div>
				</div>
			</div>
		);
	};
	
	const renderProfileCard = () => {
		return (
			<ProfileCard
				customCover={
					<div className={styles.profileCardCover}>
						{getIconByResourceType(resource?._type)}
					</div>
				}
				showData
				fitContainer
				data={{ imageId: '', name: resource?.title || '' }}
			>
				<div className={styles.profileCardContent}>
					<div className={styles.contentItem}>
						{resource?.researchFields.map((item, index) => (
							<AntdTag style={{ textTransform: 'uppercase' }} key={index}>
								{item}
							</AntdTag>
						))}
					</div>
					{renderAuthor()}
					<div className={styles.contentItem}>
						{resource?.participants.map((item, index) => (
							<div
								className={styles.participant}
								key={index}
								onClick={onClickUser.bind(null, item)}
							>
								<div className={styles.avatar}>
									{item.imageUrl && !item.isDeleted ? (
										<img src={item.imageUrl} alt=""/>
									) : (
										<Icon
											name="user"
											color="white"
											width="12px"
											height="12px"
										/>
									)}
								</div>
								<div>
									<span>
										{!item.isDeleted ?  <>{item.firstName} {item.lastName}</> : <FormattedMessage id="userLabels.deletedUser" />}
									</span>
									<div>
										<Tag color={getTagColorByRole(item.role)}><FormattedMessage id={`role.${item.role}`} /></Tag>
									</div>
								</div>
							</div>
						))}
					</div>
					{resource?._type === ResourceType.Text ? <Button
						className={styles.downloadBtn}
						type="default"
						size="large"
						href={resource?.downloadLink}
						download
						onClick={onDownload}
					>
						<FormattedMessage id="buttons.downloadResource" />
					</Button> :
						<Button
							onClick={toggleMediaPopup}
							className={styles.downloadBtn}
							type="default"
							size="large"
						>
							<FormattedMessage id="buttons.viewResource" />
						</Button>}
				</div>
			</ProfileCard>
		);
	}

	const renderInput = (formField: FormField, field: ControllerRenderProps<any>) => {
		return <Input
			{...field}
			placeholder={formField.placeholder || intl.formatMessage({ id: `formPlaceholders.${formField.id}` })}
		/>

	};
	const renderSelect = (formField: FormField, field: ControllerRenderProps<any>) => {
		return <Select
			{...field}
			filterOption={(input, option: any) =>
				option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
			}
			optionFilterProp="children"
			placeholder={formField.placeholder || intl.formatMessage({ id: `formPlaceholders.${formField.id}` })}
			style={{ width: '100%' }}
			showSearch
		>
			{formField.options && formField.options.optionList.map((option: any, index: number) => (
				<Select.Option
					key={`option${formField.id}${index}`}
					value={option.value}
				>
					{option.label}
				</Select.Option>
			))}
		</Select>
	}
	
	const renderTextArea = (formField: FormField, field: ControllerRenderProps<any>) => {
		return <TextArea
			{...field}
			placeholder={formField.placeholder || intl.formatMessage({ id: `formPlaceholders.${formField.id}` })}
			rows={6}
		/>
	};

	const renderTags = (formField: FormField, field: ControllerRenderProps<any>) => {
		return <Select
			{...field}
			mode="tags"
			placeholder={formField.placeholder || intl.formatMessage({ id: `formPlaceholders.${formField.id}` })}
			style={{ width: '100%' }}
			tagRender={({ label }) => (
				<AntdTag
					style={{
						border: 'none',
						textTransform: 'uppercase'
					}}
				>
					{label}
				</AntdTag>
			)}
		>
			{
				formField.options && formField.options.optionList.map((option: any, index: number) =>
					<Select.Option value={option.value} key={`option${formField.id}${index}`}>
						{formField.options?.type === 'codeBased' ? option.label : intl.formatMessage({ id: `${formField.options?.alternateTranslationPrefix ? formField.options?.alternateTranslationPrefix : formField.id}.${option.label}` })}
					</Select.Option>
				)
			}
		</Select>
	};

	const renderFieldByType = (formField: FormField, field: ControllerRenderProps<any>) => {
		const { type } = formField;
		switch (type) {
			case 'select': return renderSelect(formField, field);
			case 'text': return renderInput(formField, field);
			case 'textArea': return renderTextArea(formField, field);
			case 'tags': return renderTags(formField, field);
			default: return renderInput(formField, field);
		}
	}
	
	const renderField = (formField: FormField) => {
		return <div className={`${styles.fieldWrapper} ${styles[formField.layout]}`}
			key={formField.id}>
			<label className={styles.floatingLabel}>
				{formField.label || <FormattedMessage id={`userLabels.${formField.id}`} />}
			</label><Controller
				name={formField.id}
				control={control}
				rules={formField.rules}
				render={({ field }) => { return renderFieldByType(formField, field); }}
			/>
		</div>
	}

	const renderResourceInformationFields = () => {
		return <div className={styles.category} >
			<div className={styles.categoryTitle}>
				<Icon name="file-alt" color="light" type="solid" />
				<h2><FormattedMessage id="resourcesLabels.resourceInformation" /></h2>
			</div>
			<div className={styles.categoryContent}>
				{resourceInformationFields.map((formField: FormField, index) => renderField(formField))}
			</div>
		</div>
	}

	const renderResearchAreaField = () => {
		return <div className={styles.category} >
			<div className={styles.categoryTitle}>
				<Icon name="flask-potion" color="light" />
				<h2><FormattedMessage id="resourcesLabels.researchArea" /></h2>
			</div>
			<div className={styles.categoryContent}>
				<div className={`${styles.fieldWrapper} ${styles.w100}`}
			key="keywords">
			<label className={styles.floatingLabel}>
				<FormattedMessage id={`userLabels.keywords`} />
			</label><Controller
				name="researchFields"
				control={control}
						render={({ field }) => <Select
							{...field}
							mode="tags"
			placeholder={intl.formatMessage({ id: `formPlaceholders.keywords` })}
			style={{ width: '100%' }}
			tagRender={({ label }) => (
				<AntdTag
					style={{
						border: 'none',
						textTransform: 'uppercase'
					}}
				>
					{label}
				</AntdTag>
			)}>
							{mentoringScopes.map(ms => <Select.Option value={ms}>{ms}</Select.Option>)}
				</Select>}
			/>
		</div>
			</div>
		</div>
	}
	
	const renderParticipantsInput = () => {
		return <div className={styles.category} >
			<div className={styles.categoryTitle}>
				<Icon name="user-tie" color="light" />
				<h2><FormattedMessage id="resourcesLabels.participants" /></h2>
			</div>
			<div className={styles.categoryContent}>
				<div className={styles.flexContent}>
					{participants.map((item, index) =>
						<div className={styles.participant}>
							<span className={styles.name}>{item.firstName} {item.lastName}</span>
							<Tag color={getTagColorByRole(item.role)}><FormattedMessage id={`role.${item.role}`} /></Tag>
							<Button type="link" onClick={removeParticipant.bind(null, item)}>
								<Icon
									color="red"
									height="16px"
									name="times"
									width="16px"
								/>
							</Button>
						</div>)}
				</div>
				<Popover
					content={
						<Tabs
							className={styles.popoverTab}
							centered
							defaultActiveKey="1"
							size="small"
						>
							<Tabs.TabPane tab={intl.formatMessage({id: "role.Mentors"})} key="1">
								{members.filter(item => item.role === 'Mentor').map((participant, index) => {
									return <div className={styles.popoverUser} key={`mentor${index}`} onClick={addParticipant.bind(null, participant)}>
										<div className={styles.avatar}>
											{participant.imageUrl ?
												<img alt="Profile" src={participant.imageUrl} /> :
												<Icon name="user" width="12px" color="white" />}
										</div>
										{participant.firstName} {participant.lastName}
									</div>
								})}
							</Tabs.TabPane>
							<Tabs.TabPane tab={intl.formatMessage({id: "role.Mentees"})} key="2">
								{members.filter(item => item.role === 'Mentee').map((participant, index) => {
									return <div className={styles.popoverUser} key={`mentee${index}`} onClick={addParticipant.bind(null, participant)}>
										<div className={styles.avatar}>
											{participant.imageUrl ?
												<img alt="Profile" src={participant.imageUrl} /> :
												<Icon name="user" width="12px" color="white" />}
										</div>
										{participant.firstName} {participant.lastName}
									</div>
								})}
							</Tabs.TabPane>
							{canAddApplicantMentees() && 
									<Tabs.TabPane tab={intl.formatMessage({id: "role.ApplicantMentees"})} key="3">
								{members.filter(item => item.role === 'ApplicantMentee').map((participant, index) => {
									return <div className={styles.popoverUser} key={`applicant${index}`} onClick={addParticipant.bind(null, participant)}>
										<div className={styles.avatar}>
											{participant.imageUrl ?
												<img alt="Profile" src={participant.imageUrl} /> :
												<Icon name="user" width="12px" color="white" />}
										</div>
										{participant.firstName} {participant.lastName}
									</div>
								})}
							</Tabs.TabPane>
							}
						</Tabs>}
					placement="rightTop"
					trigger="click"
				>
					<button className={styles.addBtn}><FormattedMessage id="buttons.addParticipants" /></button>
				</Popover>
			</div>
		</div>
	}

	const renderCommentsCheckbox = () => {
		return (
			<div className={styles.category} >
				<div className={styles.categoryTitle}>
					<Icon name="comment-alt" color="light" type="solid" />
					<h2><FormattedMessage id="resourcesLabels.comments" /></h2>
				</div>
				<div className={styles.categoryContent}>
					<Controller
						control={control}
						name="acceptsComments"
						render={({ field }) => <Checkbox {...field} checked={field.value}>
							<FormattedMessage id="resourcesLabels.acceptsComments" />
						</Checkbox>}
					/>
				</div>
			</div>);
	}

	const renderActions = () => {
		return (<>
			{isAllowedToEdit() &&
				<>
					<Button size="large" type="default" onClick={toggleDeletePopup}> <FormattedMessage id="buttons.delete" /></Button>

					{!showEditForm && resource?.status === Status.Published && isAdmin() &&
						<Button size="large" type="default" onClick={unpublishResource}> <FormattedMessage id="buttons.unpublish" /></Button>}

					{!showEditForm && resource?.status === Status.Unpublished && isAdmin() &&
						<Button size="large" type="default" onClick={publishResource}> <FormattedMessage id="buttons.publish" /></Button>}

					{!showEditForm &&
						<Button size="large" type="primary" onClick={toggleEditForm}>
							<FormattedMessage id="buttons.editResource" />
						</Button>}

					{showEditForm &&
						<>
							<Button size="large" type="default" onClick={onCancelForm}><FormattedMessage id="buttons.cancel" /></Button>
							<Button size="large" type="primary" onClick={handleSubmit(onSubmitForm)}>
								<FormattedMessage id="buttons.save" />
							</Button> </>}

				</>}
		</>)
	}

	return (
		<div className={styles.pageContainer}>
			<div
				className={`${styles.pageSider} ${styles.topBorder} ${styles.noPadding}`}
			>
				{resource && renderProfileCard()}
			</div>
			<div className={styles.pageContent}>
				<div
					className={`${styles.mobileTopper} ${styles.topBorder} ${styles.noPadding}`}
				>
					{resource && renderProfileCard()}
				</div>
				<div className={styles.mainActions}>
					<div className={styles.breadcrumbs}>
						<Link to={getBreadCrumbLink()}>
							<FormattedMessage id="navigation.resources" />:{' '}
							{isAdmin() && resource?.status === Status.Published && (
								<FormattedMessage id="resources.published" />
							)}
							{isAdmin() && resource?.status === Status.Unpublished && (
								<FormattedMessage id="resources.unpublished" />
							)}
							{!isAdmin() && isCreator() && (
								<FormattedMessage id="resources.personal" />
							)}
							{!isAllowedToEdit() && <FormattedMessage id="resources.all" />}/{' '}
						</Link>
						<b>{resource?.title}</b>
					</div>
					<div className={styles.actions}>{renderActions()}</div>
				</div>
				{!showEditForm && resource && (
					<div className={styles.publishDateWrapper}>
						<FormattedMessage id="resources.publishedOn" />
						<span className={styles.highlight}>
							{' '}
							{moment(resource.createdAt).format("DD MMM YYYY")}{' '}
						</span>
						<FormattedMessage id="basics.by" />
						<span className={styles.highlight} onClick={onClickUser.bind(null, resource.createdBy)}>
							{' '}
							{!resource.createdBy.isDeleted ? <>	{resource?.createdBy.firstName} {resource.createdBy.lastName}</> : <FormattedMessage id="userLabels.deletedUser" />}
						</span>
						<span className={styles.tag}>
							{resource && (
								<Tag color={getTagColorByRole(resource.createdBy.role)}>
									<FormattedMessage id={`role.${resource.createdBy.role}`} />
								</Tag>
							)}
						</span>
					</div>
				)}
				{!showEditForm ? (
					<div className={styles.content}>
						<Spinner spinning={loading} />
						<div className={styles.category}>
							<div className={styles.categoryTitle}>
								<Icon
									name="file-alt"
									type="solid"
									color="light"
									width="16px"
									height="16px"
								/>
								<h2>
									<FormattedMessage id="resourcesLabels.description" />
								</h2>
							</div>
							<div className={styles.categoryContent}>
								<p>{resource?.description}</p>
							</div>
						</div>
						<div className={styles.category}>
							<div className={styles.categoryTitle}>
								<Icon
									name="comment-alt"
									type="solid"
									color="light"
									width="16px"
									height="16px"
								/>
								<h2>
									<FormattedMessage id="resourcesLabels.comments" />
								</h2>
							</div>
							<div className={styles.categoryContent}>
								{resource?.acceptsComments && !showCommentInput && (
									<button
										className={styles.addBtn}
										onClick={toggleCommentInput}
									>
										<FormattedMessage id="buttons.addComment" />
									</button>
								)}
								{showCommentInput &&
									renderCommentInput(
										{
											fullName: currentUserFullName,
											imageUrl: currentUserImageId,
										},
										toggleCommentInput,
										addComment
									)}
								{resourceComments && renderComments(resourceComments)}
							</div>
						</div>
					</div>
				) : (
					<div className={styles.content}>
						{renderResourceInformationFields()}
						{renderResearchAreaField()}
						{renderParticipantsInput()}
						{renderCommentsCheckbox()}
					</div>
				)}
			</div>
			{showDeletePopup && (
				<Popup onClose={toggleDeletePopup} title={{ action: '', target: '' }}>
					<div className={styles.popupContent}>
						<Icon name="trash" color="accent" width="100px" height="100px" />
						<h1>
							<FormattedMessage id="popupTitles.deleteConfirmation" />
						</h1>
						<p>
							<FormattedMessage id="resources.deleteConfirmation" />
						</p>
						<div className={styles.popupActions}>
							<Button size="large" type="primary" onClick={deleteResource}>
								<FormattedMessage id="buttons.delete" />
							</Button>
							<Button size="large" type="default" onClick={toggleDeletePopup}>
								<FormattedMessage id="buttons.cancel" />
							</Button>
						</div>
					</div>
				</Popup>
			)}
			{showMediaPopup && (resource?._type === "Video" || resource?._type === "Audio") &&
				<Popup onClose={toggleMediaPopup} title={{ action: '', target: '' }} size="extra-large">
			
				<MediaPlayer onReady={onMediaReady} url={resource.downloadLink} type={resource._type}/>
					
			</Popup>}
		</div>
	);
}
export default ResourceDetail;